import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, ArrowUpDown, Filter, Loader2 } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  MetricsRow, CompetencyChart, TopPerformersCard, AlertsCard,
} from '../../../components/LevelDashboard';
import Pagination from '../../../../../shared/components/Pagination';
import { useGetKvsResult6to8SchoolDetailQuery } from '../../../services/kvsResult6to8Api';
import { CLASS_NAMES } from '../data/index';
import type { LevelMetrics, CompetencyBucket, TopPerformer, AlertItem } from '../../../types';

type SortKey = 'rollNo' | 'marks' | 'attendance';
type StudentLevelFilter = 'Excellent' | 'Good' | 'Average' | 'Below Average';

const LEVEL_SLUG_TO_FILTER: Record<string, StudentLevelFilter> = {
  excellent: 'Excellent', good: 'Good', average: 'Average', 'below-average': 'Below Average',
};
const levelToSlug = (level: StudentLevelFilter) => level.toLowerCase().replace(/\s+/g, '-');
const getLevel = (marks: number): StudentLevelFilter =>
  marks >= 85 ? 'Excellent' : marks >= 70 ? 'Good' : marks >= 55 ? 'Average' : 'Below Average';

const ClassDrillDown: React.FC = () => {
  const { regionId, schoolId, classId } = useParams<{ regionId: string; schoolId: string; classId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const academicYear = Number(searchParams.get('year') || '2025');
  const termValue = searchParams.get('term') || 'overall';
  const term = termValue === 'overall' ? 2 : Number(termValue);
  const levelFilter = LEVEL_SLUG_TO_FILTER[(searchParams.get('studentLevel') || '').toLowerCase()] || undefined;

  const buildQuery = (nextLevel?: StudentLevelFilter, nextTerm?: string) => {
    const p = new URLSearchParams();
    p.set('year', String(academicYear));
    p.set('term', nextTerm ?? termValue);
    if (nextLevel) p.set('studentLevel', levelToSlug(nextLevel));
    return `?${p.toString()}`;
  };

  const { data: schoolRes, isLoading } = useGetKvsResult6to8SchoolDetailQuery(
    schoolId ? { year: academicYear, term, kvid: Number(schoolId) } : skipToken
  );

  const d = schoolRes?.data;
  const classData = d?.byClass?.[classId!];

  const students = useMemo(() => {
    if (!classData || !classData.students) return [];
    return classData.students.map(s => ({
      studentID: s.studentID, studentName: s.studentName || '', rollNo: s.rollNo,
      section: s.section, marks: s.marks,
      attendance: s.workingDay > 0 ? +((s.presentDay / s.workingDay) * 100).toFixed(1) : 0,
      level: getLevel(s.marks), needsSupport: s.marks < 60,
    }));
  }, [classData]);

  const metrics = useMemo((): LevelMetrics => {
    if (!classData) return { totalStudents: 0, avgAttendance: 0, avgCompetency: 0, needsSupportCount: 0, excellentCount: 0, avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0 };
    const total = students.length;
    const avgAttendance = total > 0 ? +(students.reduce((s, st) => s + st.attendance, 0) / total).toFixed(1) : 0;
    const needsSupportCount = students.filter(s => s.level === 'Below Average').length;
    const excellentCount = students.filter(s => s.level === 'Excellent').length;
    return { totalStudents: classData.totalStudents, avgCompetency: +classData.avgMarks.toFixed(1), needsSupportCount, excellentCount, avgAttendance, avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0 };
  }, [classData, students]);

  const competencyBuckets = useMemo((): CompetencyBucket[] => {
    const total = students.length;
    if (total === 0) return [{ level: 'Excellent', count: 0, percent: 0 }, { level: 'Good', count: 0, percent: 0 }, { level: 'Average', count: 0, percent: 0 }, { level: 'Below Average', count: 0, percent: 0 }];
    const pct = (n: number) => +((n / total) * 100).toFixed(1);
    const counts = { Excellent: 0, Good: 0, Average: 0, 'Below Average': 0 };
    students.forEach(s => { counts[s.level]++; });
    return [
      { level: 'Excellent', count: counts.Excellent, percent: pct(counts.Excellent) },
      { level: 'Good', count: counts.Good, percent: pct(counts.Good) },
      { level: 'Average', count: counts.Average, percent: pct(counts.Average) },
      { level: 'Below Average', count: counts['Below Average'], percent: pct(counts['Below Average']) },
    ];
  }, [students]);

  const topPerformers = useMemo((): TopPerformer[] =>
    [...students].sort((a, b) => b.marks - a.marks).slice(0, 5).map(s => ({
      id: String(s.studentID), name: s.studentName || `Roll No ${s.rollNo} (Sec ${s.section})`, metric: 'Marks', value: s.marks,
    })), [students]);

  const alerts = useMemo((): AlertItem[] =>
    students.filter(s => s.marks < 60).sort((a, b) => a.marks - b.marks).slice(0, 5).map(s => ({
      id: String(s.studentID), name: s.studentName || `Roll No ${s.rollNo} (Sec ${s.section})`,
      issue: `${s.marks.toFixed(1)}%`, severity: (s.marks < 40 ? 'high' : 'medium') as AlertItem['severity'],
    })), [students]);

  const levelCounts = useMemo(() => ({
    Excellent: students.filter(s => s.level === 'Excellent').length,
    Good: students.filter(s => s.level === 'Good').length,
    Average: students.filter(s => s.level === 'Average').length,
    'Below Average': students.filter(s => s.level === 'Below Average').length,
  }), [students]);

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('marks');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const filteredStudents = useMemo(() => {
    let list = [...students];
    if (search) { const q = search.toLowerCase(); list = list.filter(s => s.studentName.toLowerCase().includes(q) || String(s.rollNo || '').includes(q) || s.section.toLowerCase().includes(q)); }
    if (levelFilter) list = list.filter(s => s.level === levelFilter);
    list.sort((a, b) => { const va = a[sortKey], vb = b[sortKey]; return sortAsc ? (va as number) - (vb as number) : (vb as number) - (va as number); });
    return list;
  }, [students, search, levelFilter, sortKey, sortAsc]);

  const totalPages = Math.ceil(filteredStudents.length / pageSize);
  const pageStudents = filteredStudents.slice((page - 1) * pageSize, page * pageSize);
  const toggleSort = (key: SortKey) => { if (sortKey === key) setSortAsc(!sortAsc); else { setSortKey(key); setSortAsc(false); } };

  const SortHeader: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-indigo-600 select-none" onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-1">{label} <ArrowUpDown size={12} className={sortKey === k ? 'text-indigo-600' : 'text-gray-300'} /></span>
    </th>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <span className="ml-3 text-gray-500">Loading class data…</span>
      </div>
    );
  }

  if (!classData) {
    return (<div className="p-6 text-center"><p className="text-gray-500">Class not found.</p><button onClick={() => navigate(-1)} className="text-indigo-600 text-sm mt-2 hover:underline">Go back</button></div>);
  }

  const className = CLASS_NAMES[classId!] || `Class ${classId}`;

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(location.pathname.slice(0, location.pathname.lastIndexOf(`/class/${classId}`)) + location.search)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{className}</h1>
          <p className="text-sm text-gray-500">Middle Stage &mdash; {classData.totalStudents} students</p>
        </div>
        <div className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
          <select id="term" value={termValue} onChange={(e) => navigate(`${location.pathname}${buildQuery(levelFilter, e.target.value)}`)} className="text-sm text-gray-700 bg-transparent focus:outline-none">
            <option value="overall">Overall</option><option value="1">Term 1</option><option value="2">Term 2</option>
          </select>
        </div>
      </div>

      <MetricsRow m={metrics} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <CompetencyChart buckets={competencyBuckets} onBarClick={(level) => navigate(`${location.pathname}${buildQuery(level as StudentLevelFilter)}`)} />
        <TopPerformersCard items={topPerformers} />
        <AlertsCard items={alerts} onViewAll={() => navigate(`${location.pathname}${buildQuery('Below Average')}`)} viewAllLabel="Need Attention Students" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">Students ({filteredStudents.length})</h3>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search name / roll no…" className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 w-56" />
          </div>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          {(['Excellent', 'Good', 'Average', 'Below Average'] as StudentLevelFilter[]).map((lvl) => (
            <button key={lvl} onClick={() => { navigate(`${location.pathname}${buildQuery(levelFilter === lvl ? undefined : lvl)}`); setPage(1); }}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${levelFilter === lvl ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>{lvl} ({levelCounts[lvl]})</button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Student</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Section</th>
              <SortHeader label="Marks %" k="marks" /><SortHeader label="Attendance %" k="attendance" />
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Level</th>
            </tr></thead>
            <tbody>
              {pageStudents.map(s => (
                <tr key={s.studentID} className="border-b border-gray-100 hover:bg-indigo-50/40 cursor-pointer transition" onClick={() => navigate(`students/${s.studentID}/overview${buildQuery()}`)}>
                  <td className="px-4 py-3"><p className="text-sm font-medium text-gray-900">{s.studentName || '—'}</p><p className="text-xs text-gray-400">Roll {s.rollNo ?? '—'} · Sec {s.section}</p></td>
                  <td className="px-4 py-3 text-sm text-gray-700">{s.section}</td>
                  <td className="px-4 py-3"><span className={`text-sm font-semibold ${s.marks >= 80 ? 'text-emerald-600' : s.marks >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{s.marks.toFixed(1)}%</span></td>
                  <td className="px-4 py-3"><span className={`text-sm font-semibold ${s.attendance >= 75 ? 'text-emerald-600' : 'text-amber-600'}`}>{s.attendance}%</span></td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.level === 'Excellent' ? 'bg-emerald-50 text-emerald-700' : s.level === 'Good' ? 'bg-indigo-50 text-indigo-700' : s.level === 'Average' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{s.level}</span></td>
                </tr>
              ))}
              {pageStudents.length === 0 && (<tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">No students found.</td></tr>)}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (<div className="px-5 py-3 border-t border-gray-100"><Pagination currentPage={page} totalPages={totalPages} totalRecords={filteredStudents.length} pageSize={pageSize} onPageChange={setPage} /></div>)}
      </div>
    </div>
  );
};

export default ClassDrillDown;
